/**
 * Quellen = App-Integrationen, E-Mail-Queues, kuenftige Kanaele (Spec 3.2).
 * Eine Quelle liefert in genau **eine** Queue (`default_queue_id`).
 *
 * Phase 3 ergaenzt die vier Felder aus Spec 3.2 / 5.3, die den Rueckkanal und
 * die Metadaten ausmachen:
 *
 * | Feld | Wofuer |
 * |---|---|
 * | `webhook_url` | Ziel der Rueckkanal-Ereignisse (Spec 5.3) |
 * | `webhook_secret` | HMAC-Schluessel — **write-only**, verschluesselt abgelegt |
 * | `metadata_schema` | erwartete Metadaten je Quelle (Spec 5.2) |
 * | `metadata_display` | Anzeige-Template im Ticket (Label, Reihenfolge, Format) |
 *
 * Der API-Key wird ausschliesslich gehasht gespeichert (Spec 11); den Klartext
 * gibt es genau einmal — bei der Erzeugung bzw. beim Rotieren. Gleiche Regel
 * fuer `webhook_secret`: rein geht Klartext, raus geht nur `has_webhook_secret`.
 */
import { z } from 'zod';
import { metadataDisplaySchema, metadataSchemaSchema } from '../metadata.js';
import { uuidSchema } from './common.js';
export const SOURCE_TYPES = ['app', 'email', 'custom'];
export const sourceTypeSchema = z.enum(SOURCE_TYPES);
/**
 * Optionen je Quelle. `auto_triage` ist die Option aus Spec 9.3 und steht
 * **standardmaessig aus**: nur wo ein Mandant sie bewusst einschaltet, laeuft
 * bei einem neuen Ticket ueberhaupt ein KI-Job.
 */
export const sourceSettingsSchema = z.object({
    auto_triage: z.boolean().default(false),
});
export const DEFAULT_SOURCE_SETTINGS = { auto_triage: false };
/** Praefix des Quellen-API-Keys — im Log und im Support sofort erkennbar. */
export const SOURCE_API_KEY_PREFIX = 'th_live_';
/** Webhook-Ziele muessen absolut und http(s) sein — sonst laeuft der Job ins Leere. */
export const webhookUrlSchema = z
    .url({ protocol: /^https?$/ })
    .max(2000)
    .refine((value) => !value.includes(' '), 'Die URL darf keine Leerzeichen enthalten.');
export const sourceSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    type: sourceTypeSchema,
    name: z.string().min(1).max(200),
    default_queue_id: uuidSchema,
    settings: sourceSettingsSchema,
    /** Ob ein API-Key hinterlegt ist — der Hash selbst verlaesst nie den Server. */
    has_api_key: z.boolean(),
    /** Rueckkanal (Spec 5.3). `null` = die Quelle bekommt keine Ereignisse. */
    webhook_url: webhookUrlSchema.nullable(),
    /** Ob ein Signatur-Geheimnis hinterlegt ist — der Wert selbst nie. */
    has_webhook_secret: z.boolean(),
    metadata_schema: metadataSchemaSchema.nullable(),
    metadata_display: metadataDisplaySchema.nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createSourceSchema = z.object({
    type: sourceTypeSchema,
    name: z.string().min(1).max(200),
    default_queue_id: uuidSchema,
    settings: sourceSettingsSchema.partial().optional(),
});
/**
 * Aenderungen an einer Quelle. Ein **weggelassenes** Feld bleibt unveraendert,
 * `null` loescht es. `webhook_secret` ist write-only: `undefined` behaelt das
 * gespeicherte Geheimnis, ein String ersetzt es, `null` entfernt es.
 */
export const updateSourceSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    default_queue_id: uuidSchema.optional(),
    settings: sourceSettingsSchema.partial().optional(),
    webhook_url: webhookUrlSchema.nullable().optional(),
    webhook_secret: z.string().min(16).max(200).nullable().optional(),
    metadata_schema: metadataSchemaSchema.nullable().optional(),
    metadata_display: metadataDisplaySchema.nullable().optional(),
});
/** `api_key` ist **einmalig** gesetzt: bei der Anlage einer `app`-Quelle bzw. beim Rotieren. */
export const sourceWithKeySchema = z.object({
    source: sourceSchema,
    api_key: z.string().nullable(),
});
/**
 * Ein frisch erzeugtes Webhook-Secret — genau wie der API-Key **einmalig**
 * im Klartext, danach nur noch verschluesselt in der Datenbank.
 */
export const sourceWithSecretSchema = z.object({
    source: sourceSchema,
    webhook_secret: z.string(),
});
//# sourceMappingURL=source.js.map